import { useState } from 'react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PlayCircle, Lock } from 'lucide-react';
import { Pill, Empty, useFetch, runAction } from './shared';

const prevPeriod = () => {
  const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
};

function RunRow({ r }) {
  const s = r.summary || {};
  return (
    <tr className="border-t border-slate-100 text-sm" data-testid={`dian-close-row-${r.run_id}`}>
      <td className="py-2 pr-3 font-mono">{r.period}</td>
      <td className="py-2 pr-3">{r.mode}</td>
      <td className="py-2 pr-3"><Pill value={r.status} /></td>
      <td className="py-2 pr-3 font-mono text-xs">{s.invoices_created ?? '—'}</td>
      <td className="py-2 pr-3 font-mono text-xs">{s.excluded_refunded_payouts ?? '—'}</td>
      <td className="py-2 pr-3 font-mono text-xs">{s.errors ?? '—'}</td>
      <td className="py-2 pr-3 text-xs">{r.trigger} · {r.actor || '—'}</td>
      <td className="py-2 text-xs">{formatDate(r.started_at)}</td>
    </tr>
  );
}

export function DianClosePanel({ onChanged }) {
  const [period, setPeriod] = useState(prevPeriod());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data, load } = useFetch('/admin/dian/close-runs?limit=50');
  const after = () => { load(); onChanged && onChanged(); };
  const run = (mode) => runAction(
    () => api.post(`/admin/dian/close-runs?period=${period}&mode=${mode}${mode === 'real' ? '&confirm=true' : ''}`),
    mode === 'real' ? 'Cierre real ejecutado (solo borradores)' : 'Simulación registrada', after);
  const items = data?.items || [];
  return (
    <div className="space-y-4" data-testid="dian-close-panel">
      <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Periodo (AAAA-MM)</label>
          <Input data-testid="dian-close-period-input" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="rounded-none w-36 font-mono" />
        </div>
        <Button className="rounded-none" data-testid="dian-close-simulate-btn" onClick={() => run('simulation')}>
          <PlayCircle className="h-4 w-4 mr-1" /> Simular cierre
        </Button>
        <Button variant="outline" className="rounded-none" data-testid="dian-close-real-btn" onClick={() => setConfirmOpen(true)}>
          <Lock className="h-4 w-4 mr-1" /> Cierre real
        </Button>
        <p className="text-xs text-slate-500 basis-full">
          El cierre real crea facturas en borrador y bloquea el periodo. Nunca envía a Factus; el envío es un paso aparte.
        </p>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="dian-close-real-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cierre real {period}</AlertDialogTitle>
            <AlertDialogDescription>
              Se crearán las facturas de comisión del periodo en borrador y el periodo quedará cerrado. No se envía nada a la DIAN.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dian-close-real-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="dian-close-real-confirm" onClick={() => run('real')}>Ejecutar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {items.length === 0 ? <Empty testId="dian-close-empty">Aún no hay cierres registrados.</Empty> : (
        <div className="overflow-x-auto border border-slate-200 bg-white p-3">
          <table className="w-full text-left" data-testid="dian-close-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th>Periodo</th><th>Modo</th><th>Estado</th><th>Facturas</th><th>Excl. reembolso</th><th>Errores</th><th>Origen</th><th>Inicio</th></tr>
            </thead>
            <tbody>{items.map((r) => <RunRow key={r.run_id} r={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
